import {Injectable} from '@angular/core';
import {map, Observable, of, tap} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ApiService} from '@core/api/api.service';
import {AutoCompleteResult, PaginatedResult, PaginatedSearch} from '@core/api/common';
import {User} from '@domain/user/user';
import {NotificationService} from '@shared/notification/notification.service';
import {ApiUtil} from '@shared/util/apiUtil';

@Injectable({
  providedIn: 'root'
})
export class UserApi {

  constructor(
    private readonly http: HttpClient,
    private readonly notifyService: NotificationService,
    private readonly apiService: ApiService
  ) { }

  public get(id?: string): Observable<User | null> {
    if (!id) {
      return of(null);
    }
    return this.apiService.getOne<User>(ApiUtil.buildUrl(this.getPath(id)));
  }

  public autocomplete(term?: string): Observable<AutoCompleteResult<Partial<User>>> {
    return this.apiService.autocomplete<Partial<User>>(this.getPath(), term);
  }

  public list(options: PaginatedSearch = {}): Observable<PaginatedResult<User>> {
    return this.apiService.paginateGet<User>(ApiUtil.buildUrl(this.getPath()), options);
  }

  public update(user: Partial<User>): Observable<User> {
    const userId = user.id;
    if (!userId) {
      throw new Error('No user id?');
    }
    delete user.id;
    return this.http.patch<User>(ApiUtil.buildUrl(this.getPath(userId)), user);
  }

  public requestDelete(userId: string): Observable<any> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(userId), '/request-delete'), {});
  }

  public delete(userId: string, informUser: boolean): Observable<boolean> {
    const params = new HttpParams().set('informUser', informUser);
    return this.http.delete(ApiUtil.buildUrl(this.getPath(userId)), {params: params}).pipe(
      tap(() => {
        this.notifyService.toast({
          message: 'L\'utilisateur a été supprimé.'
        });
      }),
      map(() => true)
    );
  }

  public setCurrentMembership(membershipId: string): Observable<void> {
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(), `/set-current-membership/${membershipId}`), {});
  }

  private getPath(userId?: string): string {
    const prefix = 'user';
    return userId
      ? `${prefix}/${userId}`
      : prefix;
  }
}
